import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { 
  Plus, 
  Trash2, 
  Edit2, 
  Video, 
  Eye, 
  EyeOff,
  GripVertical,
  Loader2,
  ArrowLeft,
  Save,
  X,
  AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminShowcase = () => {
  const navigate = useNavigate();
  const { isAdmin, isAuthenticated } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    loom_url: '',
    visibility: 'public'
  });
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchVideos();
  }, [isAuthenticated, isAdmin, navigate]);

  const fetchVideos = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/videos`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setVideos(data);
      }
    } catch (err) {
      console.error('Failed to fetch videos:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFormLoading(true);

    try {
      const url = editingVideo 
        ? `${API_URL}/api/admin/videos/${editingVideo.video_id}`
        : `${API_URL}/api/admin/videos`;
      
      const response = await fetch(url, {
        method: editingVideo ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to save video');
      }

      await fetchVideos();
      resetForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/videos/${videoId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        await fetchVideos();
      }
    } catch (err) {
      console.error('Failed to delete video:', err);
    }
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      description: video.description,
      loom_url: video.loom_url,
      visibility: video.visibility
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingVideo(null);
    setFormData({
      title: '',
      description: '',
      loom_url: '',
      visibility: 'public'
    });
    setError('');
  };

  const getVisibilityIcon = (visibility) => {
    switch (visibility) {
      case 'public': return <Eye className="w-4 h-4 text-emerald-400" />;
      case 'pro': return <Eye className="w-4 h-4 text-cyan-400" />;
      case 'enterprise': return <EyeOff className="w-4 h-4 text-purple-400" />;
      default: return <Eye className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(16, 185, 129, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(16, 185, 129, 0.3) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-slate-800 bg-slate-950/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-white font-outfit">Showcase Admin</h1>
              <p className="text-sm text-slate-400">Manage demo videos</p>
            </div>
          </div>

          <Button
            onClick={() => setShowForm(true)}
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Video
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Form Modal */}
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-slate-900 border border-slate-800 rounded-2xl p-6 w-full max-w-lg"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">
                  {editingVideo ? 'Edit Video' : 'Add New Video'}
                </h2>
                <button onClick={resetForm} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full bg-slate-800/50 border border-slate-700 rounded-xl py-3 px-4 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500/50 transition-colors"
                    placeholder="Video title"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">Loom URL</label>
                  <input
                    type="url"
                    value={formData.loom_url}
                    onChange={(e) => setFormData({ ...formData, loom_url: e.target.value })}
                    className="w-full bg-slate-800/50 border border-slate-700 rounded-xl py-3 px-4 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500/50 transition-colors"
                    placeholder="https://www.loom.com/share/..."
                    required
                  />
                  <p className="text-xs text-slate-500 mt-1">Paste the Loom share link</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full bg-slate-800/50 border border-slate-700 rounded-xl py-3 px-4 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500/50 transition-colors resize-none"
                    placeholder="Video description"
                    rows={3}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">Visibility</label>
                  <select
                    value={formData.visibility}
                    onChange={(e) => setFormData({ ...formData, visibility: e.target.value })}
                    className="w-full bg-slate-800/50 border border-slate-700 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-emerald-500/50 transition-colors"
                  >
                    <option value="public">Public - Everyone can view</option>
                    <option value="pro">Pro - Pro plan users only</option>
                    <option value="enterprise">Enterprise - Enterprise plan only</option>
                  </select>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetForm}
                    className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={formLoading}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white"
                  >
                    {formLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        {editingVideo ? 'Update' : 'Add Video'}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}

        {/* Videos List */}
        {videos.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-4">
              <Video className="w-8 h-8 text-slate-600" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">No Videos Yet</h3>
            <p className="text-slate-400 mb-6">Add your first showcase video to get started</p>
            <Button
              onClick={() => setShowForm(true)}
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Video
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {videos.map((video, index) => (
              <motion.div
                key={video.video_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 flex items-center gap-4"
              >
                <div className="text-slate-600 cursor-grab">
                  <GripVertical className="w-5 h-5" />
                </div>

                <div className="w-32 h-20 rounded-lg bg-slate-800 flex-shrink-0 overflow-hidden">
                  <div className="w-full h-full flex items-center justify-center">
                    <Video className="w-8 h-8 text-slate-600" />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-white truncate">{video.title}</h3>
                    {getVisibilityIcon(video.visibility)}
                    <span className="text-xs text-slate-500 capitalize">{video.visibility}</span>
                  </div>
                  <p className="text-sm text-slate-400 line-clamp-1">{video.description || 'No description'}</p>
                  <p className="text-xs text-slate-500 mt-1 truncate">{video.loom_url}</p>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(video)}
                    className="text-slate-400 hover:text-white hover:bg-slate-800"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(video.video_id)}
                    className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Quick Link */}
        <div className="mt-8 p-4 bg-slate-900/50 border border-slate-800 rounded-xl">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-white mb-1">Public Showcase Link</h3>
              <p className="text-sm text-slate-400">Share this link with anyone to view public videos</p>
            </div>
            <Link to="/showcase" target="_blank">
              <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                View Showcase
              </Button>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminShowcase;
